import React from 'react';
import { Form } from 'antd';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/utils/query-keys';
import { useDataExtractionReport } from '@/context/DataExtractionReport';
import { useCreateDataExtraction, useUpdateDataExtraction } from '@/hooks';
import { StepFooterButtons } from '@/components/Settings/DataExtraction/components/StepFooterButtons';
import { normalizeGeneralInformationPayload } from './Footer.utils';

const { useFormInstance } = Form;

export function Footer() {
	const history = useHistory();
	const form = useFormInstance();
	const queryClient = useQueryClient();

	const { isLoading: isCreatingLoading, mutateAsync: createDataExtraction } = useCreateDataExtraction();
	const { isLoading: isUpdatingLoading, mutateAsync: updateDataExtraction } = useUpdateDataExtraction();
	const { isEditing, dataExtractionReport, onNextStep, onSetDataExtractionReport } = useDataExtractionReport();

	async function handleCreateDataExtraction(values: any): Promise<void> {
		const payload = normalizeGeneralInformationPayload(values);
		const data = await createDataExtraction(payload);
		history.replace(`/settings/data-extraction/hierarchy/${data.id}`);
	}

	async function handleUpdateDataExtraction(values: any): Promise<void> {
		if (!dataExtractionReport?.id) return;

		const payload = {
			id: dataExtractionReport.id,
			...normalizeGeneralInformationPayload(values)
		};

		const data = await updateDataExtraction(payload);
		onSetDataExtractionReport(data);
		await queryClient.invalidateQueries([QUERY_KEYS.GET_DATA_EXTRACTION]);
		onNextStep();
	}

	async function handleOnSubmit(): Promise<void> {
		const { general_information: payload } = await form.validateFields();
		await (isEditing ? handleUpdateDataExtraction(payload) : handleCreateDataExtraction(payload));
	}

	function handleOnCancel(): void {
		history.goBack();
	}

	const isLoading = isCreatingLoading || isUpdatingLoading;

	return <StepFooterButtons isLoadingNextButton={isLoading} onSubmit={handleOnSubmit} onCancel={handleOnCancel} />;
}
